/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B000190 倉庫照会
 */

import { Column, Query } from "@material-table/core";
import { B000190Find, B000190FindRequest, catchRestError } from "clients/rest";
import { Box } from "@mui/material";
import {
  SText,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  STable,
  STextField,
  msgState,
} from "components";
import { labels } from "messages/labels";
import { createRef, useContext, useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { mapIds } from "services";
import { focusState, SDialogContext, useFocus } from "states";
import { useNavigate } from "react-router-dom";

// 呼び出し時のプロパティ
type Props = Partial<{
  sokoshubetsuList: string[];
  // 削除予定区分チェック除外区分
  deljogaiKbn: string;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  sokoCdFrom: string;
  sokoCdTo: string;
  name: string;
  sokoshubetsuList: string[];
  // 削除予定区分チェック除外区分(1の場合、削除予定区分を無視)
  deljogaiKbn: string;
}>;

type Data = Partial<{
  gyoNo: number;
  sokoCd: string;
  sokoNm: string;
  sokoNmr: string;
  sokoKana: string;
  sokoshubetsuKbn: number;
  logicalsokoCd: string;
  physicalsokoCd: string;
  locationkanriKbn: number;
  sakujoyoteiKbn: number;
}>;
// フィールドの初期値を設定する。
const defaultFields: Fields = {
  sokoCdFrom: "",
  sokoCdTo: "",
  name: "",
  sokoshubetsuList: [""],
  deljogaiKbn: "",
};

// テーブルを定義する。
const columns: Column<Data>[] = [
  { field: "gyoNo", title: "行　", width: "2rem", align: "right" },
  { field: "sokoCd", title: "倉庫コード", width: "3rem" },
  { field: "sokoNm", title: "倉庫名", width: "20rem" },
  { field: "sokoNmr", hidden: true },
  { field: "sokoKana", hidden: true },
  { field: "sokoshubetsuKbn", hidden: true },
  { field: "logicalsokoCd", hidden: true },
  { field: "physicalsokoCd", hidden: true },
  { field: "locationkanriKbn", hidden: true },
  { field: "sakujoyoteiKbn", hidden: true },
];

// フォーカスIDの設定
const focusId = "B000190";

//B000190倉庫照会
export const B000190 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // テーブルの値取得用Ref
  const tableRef = createRef();
  // 初回検索用のフラグ
  const [searched, setSearched] = useState(false);
  // 検索ボタン押下時に初期ページにするRef
  const isDefaultPage = useRef(false);
  // ダイアログで処理を行う関数
  const handleSubmit = useContext(SDialogContext);
  //フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });

  // フォーカス関数
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);

  // 画面遷移
  const navigate = useNavigate();

  //初期表示
  useEffect(() => {
    // 呼出し元で指定された「対象にしたい倉庫種別の設定項目」のセット
    methods.setValue("sokoshubetsuList", props.sokoshubetsuList);

    // 削除予定区分チェック除外区分処理
    if (props.deljogaiKbn) {
      methods.setValue("deljogaiKbn", props.deljogaiKbn);
    }

    // 初期フォーカス
    focus(1);
  }, []);

  // 検索ボタンクリックイベント
  const handleClickSearchButton = () => {
    // 検索可能に変更
    setSearched(true);
    // 初期ページ設定
    isDefaultPage.current = true;
    // テーブル再検索
    (tableRef.current as any).onQueryChange();
  };

  // テーブル行ダブルクリックイベント
  const handleDoubleClickTableRow = (data: Data) => {
    handleSubmit(data);
  };

  // 検索イベント
  const handleSearch = (query: Query<Data>) => {
    return new Promise<any>((resolve) => {
      // 初回検索をしない
      if (!searched) {
        resolve({
          data: [],
          page: 0,
          totalCount: 0,
        });
        return;
      }
      // 検索フォームのデータを取得する。
      const fields = methods.getValues();
      // 初期ページ表示の場合、0を設定する。
      query.page = isDefaultPage.current ? 0 : query.page;
      // 検索パラメータを作成する。
      const param: B000190FindRequest = {
        sokoCdFrom: fields.sokoCdFrom,
        sokoCdTo: fields.sokoCdTo,
        name: fields.name,
        sokoshubetsuList: fields.sokoshubetsuList,
        // 削除予定区分チェック除外区分
        deljogaiKbn: fields.deljogaiKbn,
        // ページネーションの設定
        page: query.page,
        size: query.pageSize,
        // ソートの設定
        sort: ["sokoCd,asc"],
      };
      // API呼び出し
      B000190Find(param)
        // 正常取得時
        .then((value) => {
          const offset = query.pageSize * query.page;
          resolve({
            data: mapIds(value.data.content, "gyoNo", offset),
            page: value.data.number,
            totalCount: value.data.totalElements,
          });
        })
        // エラー時
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        })
        .finally(() => {
          // 初期ページ表示をオフにする。
          isDefaultPage.current = false;
        });
    });
  };

  // レイアウト設定
  return (
    <FormProvider {...methods}>
      <SFrame title="倉庫照会" gridTemplateColumns="6rem 6rem 1rem 6rem 2rem">
        <SLabel>倉庫コード</SLabel>
        <SNumberField name="sokoCdFrom" code size={3} {...register(1)} />
        <SText>～</SText>
        <SNumberField name="sokoCdTo" code size={3} {...register(2)} />

        <Box />

        <SLabel>倉庫名</SLabel>
        <STextField
          name="name"
          size={20}
          gridColumn="span 3"
          {...register(3)}
        />
        <SNormalButton
          label={labels.buttons.search}
          gridRow="3"
          gridColumn="5"
          {...register(4)}
          onClick={handleClickSearchButton}
        />
      </SFrame>
      <SFrame>
        <STable
          keyName="gyoNo"
          data={handleSearch}
          columns={columns}
          tableRef={tableRef}
          handleDoubleClick={handleDoubleClickTableRow}
        />
      </SFrame>
    </FormProvider>
  );
};
